/**
 * Helpers for the SEO location property pages, e.g.
 *   /uk/office-space-united-kingdom            (country)
 *   /au/enterprise-office-australia            (country + one office type)
 *   /uk/england                                (state)
 *   /uk/england/london                         (city)
 *   /uk/england/london/city-of-london          (district / street / area)
 *   /uk/england/london/city-of-london/coworking-space   (+ office type)
 *
 * The URL shape is: /{country}/{state}/{city}/{district}/{officeType?}
 * where every level is optional except the country prefix, and the last
 * segment is treated as an office type only when it matches a known slug.
 *
 * Multiple office types are not encoded in the path; they live in the
 * `?workspaceType=coworking-space,private-office` query param instead (single
 * type stays in the path).
 */

import { getCountryName } from "~/lib/helpers/constants";
import {
  formatAreaQueryCityParam,
  formatAreaQueryCountryParam,
  formatAreaQueryStateParam,
  geosearchSearchStringsEquivalent,
  isAreaCityDistinctFromState,
} from "~/lib/helpers/StringHelper";

/** URL office-type slug -> internal `workspaceType` value (see `OFFICE_TYPES`). */
export const OFFICE_TYPE_SLUG_TO_VALUE: Record<string, string> = {
  "coworking-space": "coworking",
  "private-office": "private",
  "shared-office": "shared",
  "serviced-office": "serviced",
  "enterprise-office": "enterprise",
  "sublease-office": "sublet",
  "traditional-office": "conventional",
  "virtual-office": "virtual",
};

/** Internal `workspaceType` value -> URL office-type slug. */
export const OFFICE_TYPE_VALUE_TO_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(OFFICE_TYPE_SLUG_TO_VALUE).map(([slug, value]) => [value, slug]),
);

export const isOfficeTypeSlug = (segment?: string | null): boolean =>
  !!segment && segment.toLowerCase() in OFFICE_TYPE_SLUG_TO_VALUE;

/**
 * Path segments that name a PAGE, never a place — kept in sync with the
 * `($country).*` route files.
 *
 * The location tree (`$country.$state.$city.$district.$officeType`) matches any
 * 2-5 segments with no validation, so a crawled `/ly/pr/privacy-policy` was
 * parsed as state "Pr" + city "Privacy Policy" and sent to /location-polygon,
 * which spent a Google geocode before the page 404'd anyway. Bots walk these
 * combinations across every country code, so each one was billable. Rejecting
 * the path here means the loader 404s before any geocoding happens.
 */
const RESERVED_PAGE_BASES = [
  "add-listing",
  "areas",
  "blog",
  "buildings",
  "co-stats",
  "compare",
  "contact-us",
  "favorites",
  "find-perfect-workspace",
  "listings",
  "meet-the-team",
  "news",
  "office-space",
  "office-spaces",
  "privacy-policy",
  "properties",
  "propertygpt",
  "search",
  "terms-conditions",
  "testimonials",
  "workspaces",
];

// Singular AND plural of each, because near-misses get crawled just as hard:
// `/au/blogs` has no route of its own, so it fell through to the location tree
// and was geocoded as the state "Blogs".
const RESERVED_PAGE_SLUGS = new Set(
  RESERVED_PAGE_BASES.flatMap((slug) => [
    slug,
    slug.endsWith("s") ? slug.slice(0, -1) : `${slug}s`,
  ]),
);

/** True when a segment is a real page name (see `RESERVED_PAGE_SLUGS`). Matches
 * the whole segment only, so the `office-space-<country>` landing slug — which
 * merely starts with a reserved word — is unaffected. */
export const isReservedPageSlug = (segment?: string | null): boolean =>
  !!segment && RESERVED_PAGE_SLUGS.has(segment.toLowerCase());

export const officeTypeSlugToValue = (segment?: string | null): string | undefined =>
  segment ? OFFICE_TYPE_SLUG_TO_VALUE[segment.toLowerCase()] : undefined;

export const officeTypeValueToSlug = (value?: string | null): string | undefined =>
  value ? OFFICE_TYPE_VALUE_TO_SLUG[value] : undefined;

/** Normalize a slug or legacy internal token to an API workspace type value. */
export function normalizeWorkspaceTypeToken(token: string): string {
  const trimmed = token.trim();
  if (!trimmed || trimmed === "All") return trimmed;
  const lower = trimmed.toLowerCase();
  if (isOfficeTypeSlug(lower)) {
    return officeTypeSlugToValue(lower) ?? trimmed;
  }
  if (lower === "managed") return "serviced";
  return trimmed;
}

/** Internal filter/API values -> comma-separated URL slug param (e.g. `coworking-space,private-office`). */
export function officeTypeValuesToSlugParam(values: string[]): string {
  return values
    .filter((value) => value && value !== "All")
    .map((value) => normalizeWorkspaceTypeToken(value))
    .map((value) => officeTypeValueToSlug(value) ?? value)
    .filter(Boolean)
    .join(",");
}

/** URL `workspaceType` param (slugs or legacy values) -> comma-separated API values. */
export function workspaceTypeParamToApiValues(param: string): string {
  return param
    .split(",")
    .map((token) => token.trim())
    .filter(Boolean)
    .map((token) => normalizeWorkspaceTypeToken(token))
    .filter(Boolean)
    .join(",");
}

/** URL `workspaceType` param -> internal filter values for UI state. */
export function workspaceTypeParamToFilterValues(param: string): string[] {
  return param
    .split(",")
    .map((token) => token.trim())
    .filter(Boolean)
    .map((token) => normalizeWorkspaceTypeToken(token));
}

/** Resolve workspace type tokens from the query param and/or path office-type slug. */
export function resolveWorkspaceTypeTokensFromUrl(
  workspaceTypeParam?: string | null,
  pathOfficeTypeSlug?: string | null,
): string[] {
  if (workspaceTypeParam && workspaceTypeParam !== "All") {
    return workspaceTypeParamToFilterValues(workspaceTypeParam);
  }
  const slug = pathOfficeTypeSlug?.toLowerCase();
  if (slug && isOfficeTypeSlug(slug)) {
    return workspaceTypeParamToFilterValues(slug);
  }
  return [];
}

/** Workspace type param string for parsedQuery (query wins; else path slug). */
export function resolveWorkspaceTypeParamFromUrl(
  workspaceTypeParam?: string | null,
  pathOfficeTypeSlug?: string | null,
): string | undefined {
  if (workspaceTypeParam) return workspaceTypeParam;
  const slug = pathOfficeTypeSlug?.toLowerCase();
  if (slug && isOfficeTypeSlug(slug)) return slug;
  return undefined;
}

/** Country landing pages use the `office-space-<country-name>` slug pattern. */
export const COUNTRY_PAGE_PREFIX = "office-space-";

export const isCountryPageSlug = (segment?: string | null): boolean =>
  !!segment && segment.toLowerCase().startsWith(COUNTRY_PAGE_PREFIX);

/** `Australia` -> `australia`, `United Kingdom` -> `united-kingdom`. */
export function countryNamePathSlug(countryCode: string): string {
  return getCountryName((countryCode || "").toLowerCase())
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-");
}

/** `/au/enterprise-office-australia` — one office type on a country-only page. */
export function buildCountryTypedPropertyPagePath(
  countryCode: string,
  officeTypeSlug: string,
): string {
  const country = (countryCode || "").toLowerCase();
  return `/${country}/${officeTypeSlug}-${countryNamePathSlug(country)}`;
}

/** `/au/search/office-space-australia`, or `/au/search/enterprise-office-australia` for one type. */
export function buildCountrySearchPagePath(
  countryCode: string,
  officeTypeSlug?: string | null,
): string {
  const country = (countryCode || "").toLowerCase();
  const segment = officeTypeSlug
    ? `${officeTypeSlug}-${countryNamePathSlug(country)}`
    : `${COUNTRY_PAGE_PREFIX}${countryNamePathSlug(country)}`;
  return `/${country}/search/${segment}`;
}

/**
 * `office-space-australia` is the untyped country page.
 * `enterprise-office-australia` is that same page with one office type.
 * A state or city segment is not a country landing.
 */
export function parseCountryLandingSegment(
  countryCode: string,
  segment?: string | null,
): { officeTypeSlug?: string } | null {
  const lower = (segment || "").trim().toLowerCase();
  if (!lower) return null;
  if (isCountryPageSlug(lower)) return {};

  const countrySlug = countryNamePathSlug(countryCode);
  if (!countrySlug) return null;
  const suffix = `-${countrySlug}`;
  if (!lower.endsWith(suffix)) return null;

  const officeTypeSlug = lower.slice(0, -suffix.length);
  if (!isOfficeTypeSlug(officeTypeSlug)) return null;
  return { officeTypeSlug };
}

/** `city-of-london` -> `city of london` (free-text location for the polygon API). */
export const deslugToText = (slug?: string | null): string =>
  slug ? decodeURIComponent(slug).replace(/-+/g, " ").trim() : "";

/** Location display name -> URL path segment (e.g. `Sydney CBD` -> `sydney-cbd`). */
export const locationNameToSlug = (value?: string | null): string =>
  value ? decodeURIComponent(String(value)).trim().toLowerCase().replace(/\s+/g, "-") : "";

export interface PropertyBreadcrumbSegment {
  slug: string;
  label: string;
}

function formatPropertyBreadcrumbStateLabel(state: string) {
  const decoded = decodeURIComponent(state);
  return decoded.length <= 3
    ? decoded.toUpperCase()
    : decoded
        .split("-")
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(" ");
}

function formatPropertyBreadcrumbCityLabel(city: string) {
  return decodeURIComponent(city)
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

/** Build hierarchical property-page breadcrumb segments from an area summary record. */
export function buildPropertyBreadcrumbSegmentsFromArea(
  areaSummary?: {
    state?: string;
    city?: string;
    suburb?: string;
    name?: string;
  } | null,
): PropertyBreadcrumbSegment[] {
  if (!areaSummary) return [];

  const segments: PropertyBreadcrumbSegment[] = [];
  const state = areaSummary.state ? String(areaSummary.state) : "";
  const city = areaSummary.city ? String(areaSummary.city) : "";
  const suburb = areaSummary.suburb ? String(areaSummary.suburb) : "";

  const isDistinct = (value: string, parent?: string) => {
    if (!value) return false;
    const valueKey = value.trim().toLowerCase().replace(/-/g, " ");
    const parentKey = parent ? parent.trim().toLowerCase().replace(/-/g, " ") : "";
    return !parentKey || valueKey !== parentKey;
  };

  if (state) {
    segments.push({
      slug: locationNameToSlug(state),
      label: formatPropertyBreadcrumbStateLabel(state),
    });
  }

  if (city && isDistinct(city, state)) {
    segments.push({
      slug: locationNameToSlug(city),
      label: formatPropertyBreadcrumbCityLabel(city),
    });
  }

  const district = suburb && isDistinct(suburb, city || state) ? suburb : "";
  if (district) {
    segments.push({
      slug: locationNameToSlug(district),
      label: formatPropertyBreadcrumbCityLabel(district),
    });
  }

  return segments;
}

/**
 * Rewrite legacy `/search` URLs (including stored recent searches) to property
 * pages: `/{country}/{state}/{city}` or the country landing page.
 */
export function convertSearchUrlToLocationPage(url: string): string {
  if (!url) return url;

  let pathPart = url;
  let queryPart = "";

  if (/^https?:\/\//i.test(url)) {
    try {
      const parsed = new URL(url);
      pathPart = parsed.pathname;
      queryPart = parsed.search ? parsed.search.slice(1) : "";
    } catch {
      [pathPart = "", queryPart = ""] = url.split("?");
    }
  } else {
    [pathPart = "", queryPart = ""] = url.split("?");
  }

  const segments = pathPart.split("/").filter(Boolean);
  const searchIndex = segments.indexOf("search");
  if (searchIndex === -1) {
    if (/^https?:\/\//i.test(url)) {
      return queryPart ? `${pathPart}?${queryPart}` : pathPart;
    }
    return url;
  }

  const country = (searchIndex > 0 ? segments[searchIndex - 1] : "us").toLowerCase();
  const locationParts = segments.slice(searchIndex + 1);
  const basePath =
    locationParts.length > 0
      ? `/${country}/${locationParts.join("/")}`
      : `/${country}/${COUNTRY_PAGE_PREFIX}${locationNameToSlug(getCountryName(country))}`;

  return queryPart ? `${basePath}?${queryPart}` : basePath;
}

type PropertyNavQuery = Record<string, string | number | undefined | null>;

function appendPropertyNavQuery(path: string, query?: PropertyNavQuery): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value != null && value !== "") params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

/** State + suburb path segments for property nav links (collapses when state equals suburb). */
export function buildPropertyNavStateCitySegments(
  state: string,
  suburb: string,
): string[] {
  const stateSlug = locationNameToSlug(state);
  const suburbSlug = locationNameToSlug(suburb);
  if (!stateSlug) return suburbSlug ? [suburbSlug] : [];
  if (!suburbSlug || stateSlug === suburbSlug) return [stateSlug];
  return [stateSlug, suburbSlug];
}

/**
 * Property pages always include a country segment (`/us/tx/dallas`).
 * `getRegionUrlPrefix` still returns "" for the US (legacy), so normalize that here.
 */
function normalizePropertyNavRegionPrefix(regionPrefix: string): string {
  const normalized = (regionPrefix || "")
    .replace(/\/+/g, "/")
    .replace(/\/$/, "");
  return normalized || "/us";
}

/** Property page URL for a city/suburb nav link. */
export function buildPropertyNavCityUrl(
  regionPrefix: string,
  state: string,
  suburb: string,
  query?: PropertyNavQuery,
): string {
  const prefix = normalizePropertyNavRegionPrefix(regionPrefix);
  const segments = buildPropertyNavStateCitySegments(state, suburb);
  const path = `${prefix}/${segments.join("/")}`.replace(/\/+/g, "/");
  return appendPropertyNavQuery(path, query);
}

/** Property page URL for a state/region nav link. */
export function buildPropertyNavStateUrl(
  regionPrefix: string,
  state: string,
): string {
  const prefix = normalizePropertyNavRegionPrefix(regionPrefix);
  const stateSlug = locationNameToSlug(state);
  return `${prefix}/${stateSlug}`.replace(/\/+/g, "/");
}

/** Country property landing page for nav "view all" / worldwide links. */
export function buildPropertyNavCountryUrl(regionPrefix: string): string {
  const country =
    normalizePropertyNavRegionPrefix(regionPrefix).replace(/^\//, "") || "us";
  return buildCountryPropertyPagePath(country);
}

/**
 * When exactly one office type is selected, append its URL slug to the location path
 * (e.g. `…/coworking-space`) and remove `workspaceType` from the query string.
 * Multiple types remain as `?workspaceType=coworking-space,private-office`.
 */
export function buildLocationUrlWithOfficeTypeFilter(
  baseUrl: string,
  queryString: string,
  officeTypes: string[] = [],
): string {
  if (!baseUrl) return "";

  const hasProtocol = baseUrl.startsWith("http://") || baseUrl.startsWith("https://");
  const normalizedBase = hasProtocol
    ? baseUrl
    : baseUrl.startsWith("/")
      ? baseUrl
      : `/${baseUrl}`;

  const origin =
    typeof window !== "undefined" ? window.location.origin : "http://localhost";
  const urlObj = new URL(hasProtocol ? normalizedBase : `${origin}${normalizedBase}`);
  let pathname = urlObj.pathname.replace(/\/$/, "") || "/";

  const cleanQuery = (queryString || "").replace(/^[?&]/, "");
  const mergedQuery = [urlObj.search ? urlObj.search.slice(1) : "", cleanQuery]
    .filter(Boolean)
    .join("&");
  const params = new URLSearchParams(mergedQuery);

  const typesFromFilter = officeTypes.filter((type) => type && type !== "All");
  const typesFromQuery = (params.get("workspaceType") || "")
    .split(",")
    .map((type) => type.trim())
    .filter(Boolean);
  const workspaceTypes = typesFromFilter.length > 0 ? typesFromFilter : typesFromQuery;

  if (workspaceTypes.length === 1) {
    const slug = officeTypeValueToSlug(normalizeWorkspaceTypeToken(workspaceTypes[0]));
    if (slug) {
      const segments = pathname.split("/").filter(Boolean);
      const countryLanding =
        segments.length === 2
          ? parseCountryLandingSegment(segments[0], segments[1])
          : null;
      // Country-only pages use `/{country}/{office-type}-{country}`.
      // State and city pages still append the office-type slug.
      if (countryLanding) {
        pathname = buildCountryTypedPropertyPagePath(segments[0], slug);
      } else {
        const last = segments[segments.length - 1]?.toLowerCase();
        if (last && isOfficeTypeSlug(last)) {
          segments.pop();
        }
        if (segments[segments.length - 1]?.toLowerCase() !== slug) {
          segments.push(slug);
        }
        pathname = `/${segments.join("/")}`;
      }
      params.delete("workspaceType");
    }
  } else if (workspaceTypes.length > 1) {
    const slugParam = officeTypeValuesToSlugParam(workspaceTypes);
    if (slugParam) {
      const segments = pathname.split("/").filter(Boolean);
      const typedCountry =
        segments.length === 2
          ? parseCountryLandingSegment(segments[0], segments[1])?.officeTypeSlug
          : undefined;
      if (typedCountry) {
        pathname = buildCountryPropertyPagePath(segments[0]);
      } else {
        const last = segments[segments.length - 1]?.toLowerCase();
        if (last && isOfficeTypeSlug(last)) {
          segments.pop();
          pathname = `/${segments.join("/")}`;
        }
      }
      params.set("workspaceType", slugParam);
    }
  }

  const finalQuery = params.toString();
  const pathAndQuery = `${pathname}${finalQuery ? `?${finalQuery}` : ""}`;
  return hasProtocol ? `${urlObj.origin}${pathAndQuery}` : pathAndQuery;
}

export type LocationLevel = "country" | "state" | "city" | "district";

export interface ParsedLocationRoute {
  level: LocationLevel;
  isCountryPage: boolean;
  stateSlug?: string;
  citySlug?: string;
  districtSlug?: string;
  /** Office-type slug taken from the path (single type only). */
  officeTypeSlug?: string;
  /** Internal `workspaceType` value for the path office type. */
  officeTypeValue?: string;
  /** Path up to the deepest location segment (no office type), e.g. `/uk/england/london`. */
  locationBasePath: string;
}

export interface LocationRouteParams {
  country?: string;
  state?: string;
  city?: string;
  district?: string;
  officeType?: string;
}

/**
 * Resolve the route params into a structured location + office-type descriptor.
 * Returns `null` when the params can't form a valid location (caller should 404).
 */
export function parseLocationRoute(
  params: LocationRouteParams,
): ParsedLocationRoute | null {
  const country = (params.country || "").toLowerCase();
  if (!country) return null;

  const segments = [params.state, params.city, params.district, params.officeType].filter(
    (segment): segment is string => !!segment,
  );
  if (segments.length === 0) return null;

  // A page name anywhere in the path means this is not a location route at all
  // (`/ly/pr/privacy-policy`, `/bw/gd/add-listing`, `/cl/news/…`). Returning
  // null makes the loader 404 immediately, so no polygon request — and no
  // Google geocode — is ever made for it.
  if (segments.some((segment) => isReservedPageSlug(segment))) return null;

  // Country landing page: a single `office-space-*` segment after the country.
  if (segments.length === 1) {
    const countryLanding = parseCountryLandingSegment(country, segments[0]);
    if (countryLanding) {
      return {
        level: "country",
        isCountryPage: true,
        officeTypeSlug: countryLanding.officeTypeSlug,
        officeTypeValue: officeTypeSlugToValue(countryLanding.officeTypeSlug),
        // Untyped country path, so clearing the office type returns here.
        locationBasePath: countryLanding.officeTypeSlug
          ? buildCountryPropertyPagePath(country)
          : `/${country}/${segments[0]}`,
      };
    }
  }

  // The last segment is an office type only when at least the state remains.
  let officeTypeSlug: string | undefined;
  if (segments.length >= 2 && isOfficeTypeSlug(segments[segments.length - 1])) {
    officeTypeSlug = segments.pop();
  }

  const [stateSlug, citySlug, districtSlug] = segments;
  if (!stateSlug) return null;

  let level: LocationLevel = "state";
  if (districtSlug) level = "district";
  else if (citySlug) level = "city";

  const locationSegments = [stateSlug, citySlug, districtSlug].filter(Boolean) as string[];

  return {
    level,
    isCountryPage: false,
    stateSlug,
    citySlug,
    districtSlug,
    officeTypeSlug,
    officeTypeValue: officeTypeSlugToValue(officeTypeSlug),
    locationBasePath: `/${country}/${locationSegments.join("/")}`,
  };
}

export function buildCountryPropertyPagePath(countryCode: string): string {
  const country = countryCode.toLowerCase();
  return `/${country}/${COUNTRY_PAGE_PREFIX}${countryNamePathSlug(country)}`;
}

/** Country browse must land on `/ae/office-space-{country-name}`, never `/{country}` home. */
export function resolveLocationBrowseHref(
  href: string,
  countryCode?: string,
  browseLabel?: string,
): string {
  const country = (countryCode || "").trim().toLowerCase();
  if (!country) return href;
  const countryPage = buildCountryPropertyPagePath(country);
  if (browseLabel === getCountryName(country)) return countryPage;
  const homePath = `/${country}`;
  if (!href || href === homePath || href === `${homePath}/`) return countryPage;
  return href;
}

function safeDecodeURIComponent(value: string) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function formatPropertyLocationLabel(slug?: string | null): string {
  if (!slug) return "";
  const decoded = safeDecodeURIComponent(slug);
  if (decoded.length <= 3) return decoded.toUpperCase();
  return decoded
    .replace(/-+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function isSafeInternalPath(href: string) {
  return href.startsWith("/") && !href.startsWith("//") && !href.includes("://");
}

function toSafeCountryCode(value: string) {
  const code = value.trim().toLowerCase();
  return /^[a-z]{2}$/.test(code) ? code : "";
}

function toAreaQueryCity(value: string) {
  try {
    return formatAreaQueryCityParam(value);
  } catch {
    return value;
  }
}

function toAreaQueryState(value: string) {
  try {
    return formatAreaQueryStateParam(value);
  } catch {
    return value;
  }
}

function toAreaQueryCountry(value: string) {
  try {
    return formatAreaQueryCountryParam(value);
  } catch {
    return value;
  }
}

/**
 * Listing H1 / subheadline place from the location URL path.
 * Always uses the last two path segments (deepest first), e.g.
 * `/au/nsw/sydney/sydney-cbd` → "Sydney Cbd, Sydney".
 */
export function buildLocationListingPlaceLabel(options: {
  country: string;
  locationBasePath: string;
  isCountryPage?: boolean;
  countryName?: string;
}): string {
  const countryCode = options.country.toLowerCase();
  const fallbackCountry =
    options.countryName?.trim() || getCountryName(countryCode);

  if (options.isCountryPage) {
    return fallbackCountry;
  }

  const segments = options.locationBasePath.split("/").filter(Boolean);
  if (segments.length === 0) return fallbackCountry;

  const lastTwo = segments.slice(-2);
  const formatSegment = (slug: string) => {
    if (slug.toLowerCase() === countryCode) return fallbackCountry;
    return formatPropertyLocationLabel(slug);
  };

  // Deepest segment first: sydney-cbd, sydney → "Sydney Cbd, Sydney"
  return [...lastTwo].reverse().map(formatSegment).filter(Boolean).join(", ");
}

/** Free-text `?q=` address for listing headlines, e.g. "pitt street" → "Pitt Street". */
export function formatLocationAddressQueryLabel(q?: string | null): string {
  if (!q) return "";
  return decodeURIComponent(String(q))
    .replace(/\+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/** End-of-list CTA target for path-based property geosearch pages.
 * One step up: street/district → city, city → state, state → country. */
export function resolvePropertyEndOfListNavigation(options: {
  country: string;
  level: LocationLevel;
  locationBasePath: string;
  stateSlug?: string;
}): { text: string; href: string; areaPayload?: Record<string, string> } {
  const country = options.country.toLowerCase();
  const { level, locationBasePath, stateSlug } = options;
  const segments = locationBasePath.split("/").filter(Boolean);
  const pathState = stateSlug || segments[1];
  const pathCity = segments[2];
  const statePath = pathState ? `/${country}/${pathState}` : locationBasePath;
  const cityPath =
    pathState && pathCity ? `/${country}/${pathState}/${pathCity}` : undefined;

  if (level === "district" && cityPath) {
    return {
      text: formatPropertyLocationLabel(pathCity),
      href: cityPath,
    };
  }

  if (level === "district" || level === "city") {
    return {
      text: formatPropertyLocationLabel(pathState),
      href: statePath,
    };
  }

  return {
    text: getCountryName(country),
    href: buildCountryPropertyPagePath(country),
  };
}

export type ParentLocationBrowseTarget = {
  text: string;
  href: string;
  areaPayload?: Record<string, string>;
};

export function hasRealAreaSummary(areaSummary: any): boolean {
  if (!areaSummary || typeof areaSummary !== "object") return false;
  return Boolean(
    areaSummary.sfid ||
      areaSummary.id ||
      areaSummary.city ||
      areaSummary.suburb ||
      areaSummary.state,
  );
}

const AREA_BROWSE_LOCATION_RESET: Record<string, string> = {
  city: "reset",
  state: "reset",
  scope: "reset",
  page: "1",
};

/** One step up for area / search empty-state: street → city → state → country. */
export function resolveAreaSearchParentLocationBrowse(options: {
  country: string;
  urlCity?: string;
  urlState?: string;
  urlText?: string;
  isCountryScope?: boolean;
  urlHasPoint?: boolean;
  queryHasLatitude?: boolean;
  paramsCity?: string;
  paramsState?: string;
  parsedCity?: string;
  parsedState?: string;
  areaCity?: string;
  areaSuburb?: string;
  areaState?: string;
  hasAreaSummary?: boolean;
}): ParentLocationBrowseTarget {
  const safeCountry = toSafeCountryCode(options.country || "");
  const urlCity = (options.urlCity || "").trim();
  const urlState = (options.urlState || "").trim();
  const urlText = (options.urlText || "").trim();
  const resolvedState = String(
    urlState || options.paramsState || options.parsedState || options.areaState || "",
  ).trim();
  const resolvedCity = String(
    urlCity ||
      options.paramsCity ||
      options.parsedCity ||
      options.areaCity ||
      options.areaSuburb ||
      "",
  ).trim();
  const areaCity = String(options.areaCity || "").trim();
  const areaSuburb = String(options.areaSuburb || "").trim();
  const payloadCity = resolvedCity ? toAreaQueryCity(resolvedCity) : "";
  const payloadState = resolvedState ? toAreaQueryState(resolvedState) : "";
  const payloadCountry = safeCountry ? toAreaQueryCountry(safeCountry) : "";
  const countryHref = safeCountry ? buildCountryPropertyPagePath(safeCountry) : "";
  const cityHref =
    safeCountry && resolvedState && resolvedCity
      ? buildPropertyNavCityUrl(`/${safeCountry}`, resolvedState, resolvedCity)
      : countryHref;
  const stateHref =
    safeCountry && resolvedState
      ? buildPropertyNavStateUrl(`/${safeCountry}`, resolvedState)
      : countryHref;

  const cityTarget = (): ParentLocationBrowseTarget => ({
    text: formatPropertyLocationLabel(resolvedCity),
    href: cityHref,
    areaPayload:
      payloadCountry && payloadCity && payloadState
        ? {
            ...AREA_BROWSE_LOCATION_RESET,
            country: payloadCountry,
            city: payloadCity,
            state: payloadState,
          }
        : undefined,
  });
  const stateTarget = (): ParentLocationBrowseTarget => {
    if (!payloadCountry) {
      return { text: formatPropertyLocationLabel(resolvedState), href: stateHref };
    }
    if (!payloadState) {
      return {
        text: formatPropertyLocationLabel(resolvedState),
        href: stateHref,
        areaPayload: {
          ...AREA_BROWSE_LOCATION_RESET,
          country: payloadCountry,
          scope: "country",
        },
      };
    }
    return {
      text: formatPropertyLocationLabel(resolvedState),
      href: stateHref,
      areaPayload: {
        ...AREA_BROWSE_LOCATION_RESET,
        country: payloadCountry,
        state: payloadState,
      },
    };
  };
  const countryTarget = (): ParentLocationBrowseTarget => ({
    text: getCountryName(safeCountry),
    href: countryHref,
    areaPayload: payloadCountry
      ? {
          ...AREA_BROWSE_LOCATION_RESET,
          country: payloadCountry,
          scope: "country",
        }
      : undefined,
  });

  if (options.isCountryScope) {
    return { text: "", href: "" };
  }

  if (urlText) {
    if (resolvedCity) return cityTarget();
    if (resolvedState) return stateTarget();
    return countryTarget();
  }

  if (options.hasAreaSummary) {
    if (urlCity) {
      return resolvedState ? stateTarget() : countryTarget();
    }
    if (urlState) {
      return countryTarget();
    }
    if (isAreaCityDistinctFromState(areaSuburb, areaCity || resolvedCity)) {
      return resolvedCity ? cityTarget() : stateTarget();
    }
    if (resolvedState) return stateTarget();
    return countryTarget();
  }

  if (options.urlHasPoint || options.queryHasLatitude) {
    if (resolvedCity) return cityTarget();
    if (resolvedState) return stateTarget();
  }
  if (urlCity && resolvedState) return stateTarget();
  if (resolvedState) return countryTarget();
  return countryTarget();
}

/** Active geosearch filters to carry onto a property detail URL. */
export type GeosearchPropertyDetailFilterContext = {
  officeTypes?: string[];
  minPeople?: string | number;
  deskRange?: [number | null, number | null];
  officeTypeSlug?: string | null;
  parsedQuery?: Record<string, string>;
};

/** Build `?workspaceType=…&minDesk=…` from property geosearch filters (only when set). */
export function buildGeosearchPropertyDetailQuery(
  context: GeosearchPropertyDetailFilterContext,
): string {
  const params = new URLSearchParams();

  const activeTypes = (context.officeTypes ?? []).filter(
    (type) => type && type !== "All",
  );
  let workspaceTypeParam = "";
  if (activeTypes.length > 0) {
    workspaceTypeParam = officeTypeValuesToSlugParam(activeTypes);
  } else if (context.officeTypeSlug) {
    workspaceTypeParam = context.officeTypeSlug;
  } else if (context.parsedQuery?.workspaceType) {
    const fromQuery = workspaceTypeParamToFilterValues(context.parsedQuery.workspaceType);
    if (fromQuery.length > 0) {
      workspaceTypeParam = officeTypeValuesToSlugParam(fromQuery);
    }
  }
  if (workspaceTypeParam) {
    params.set("workspaceType", workspaceTypeParam);
  }

  const minPeopleRaw =
    context.minPeople ??
    (context.deskRange?.[0] != null && context.deskRange[0] > 0
      ? String(context.deskRange[0])
      : undefined) ??
    context.parsedQuery?.minPeople;
  const minDesk =
    minPeopleRaw != null && minPeopleRaw !== ""
      ? parseInt(String(minPeopleRaw), 10)
      : NaN;
  if (Number.isFinite(minDesk) && minDesk > 0) {
    params.set("minDesk", String(minDesk));
  }

  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export function appendGeosearchFiltersToPropertyPath(
  propertyPath: string,
  context: GeosearchPropertyDetailFilterContext,
): string {
  if (!propertyPath) return propertyPath;
  const query = buildGeosearchPropertyDetailQuery(context);
  return query ? `${propertyPath}${query}` : propertyPath;
}

/** Read geosearch filter params on a property detail page (`workspaceType`, `minDesk`). */
export function parsePropertyDetailGeosearchFilters(searchParams: URLSearchParams): {
  workspaceType: string[];
  minPeople: number;
} {
  const workspaceTypeRaw =
    searchParams.get("workspaceType") ?? searchParams.get("officeType");
  const minDeskParam = searchParams.get("minDesk");
  const workspaceType = workspaceTypeRaw
    ? workspaceTypeParamToFilterValues(workspaceTypeRaw)
    : [];
  const minDesk = minDeskParam ? parseInt(minDeskParam, 10) : 0;

  return {
    workspaceType,
    minPeople: Number.isFinite(minDesk) && minDesk > 0 ? minDesk : 0,
  };
}

/**
 * Navigate to a location-search URL, or revalidate when path+query are unchanged.
 * Same-street address picks keep `/au/nsw/sydney/pitt-street` while updating the
 * cookie placeId — without an explicit revalidate the loader never re-runs and
 * the map keeps the previous polygon.
 */
export function navigateOrRevalidateLocationSearch(options: {
  target: string;
  currentPathname: string;
  currentSearch: string;
  navigate: (to: string) => void;
  revalidate: () => void;
}): void {
  const { target, currentPathname, currentSearch, navigate, revalidate } = options;
  if (!target) return;

  let pathname = target;
  let search = "";
  try {
    const url = new URL(target, "http://local.invalid");
    pathname = url.pathname;
    search = url.search;
  } catch {
    const qIndex = target.indexOf("?");
    if (qIndex >= 0) {
      pathname = target.slice(0, qIndex);
      search = target.slice(qIndex);
    }
  }

  if (
    pathname === currentPathname &&
    geosearchSearchStringsEquivalent(search, currentSearch)
  ) {
    revalidate();
    return;
  }

  navigate(target);
}
